import { useRef, useEffect, useState } from 'react';
import { serviceGroups } from '@/mocks/projects';
import { Link } from 'react-router-dom';

function ServiceRow({ group, index, isEven }: { group: typeof serviceGroups[0]; index: number; isEven: boolean }) {
  const ref = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.unobserve(el);
        }
      },
      { threshold: 0.2 }
    );
    observer.observe(el);
    return () => observer.unobserve(el);
  }, []);

  const handleMouseMove = (e: React.MouseEvent) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width - 0.5) * 20;
    const y = ((e.clientY - rect.top) / rect.height - 0.5) * 20;
    setMousePos({ x, y });
  };

  const handleMouseLeave = () => {
    setMousePos({ x: 0, y: 0 });
  };

  const imageContent = (
    <div
      className="relative w-full h-full min-h-[360px] md:min-h-[520px] overflow-hidden group cursor-pointer"
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
    >
      <div
        className="absolute inset-0 bg-cover bg-center transition-transform duration-1000 ease-editorial"
        style={{
          backgroundImage: `url('${group.imageUrl}')`,
          transform: `scale(1.08) translate(${mousePos.x * 0.3}px, ${mousePos.y * 0.3}px)`,
        }}
      />
      <div className="absolute inset-0 bg-charcoal-900/15 group-hover:bg-charcoal-900/5 transition-colors duration-700" />
      <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-600">
        <div className="w-16 h-16 flex items-center justify-center border border-cream-50/40 bg-charcoal-900/30 backdrop-blur-sm">
          <i className={`${group.icon} text-2xl text-cream-50`}></i>
        </div>
      </div>
      <div className="absolute top-6 left-6">
        <span className="font-mono text-[11px] text-cream-50/70 tracking-[0.3em] uppercase bg-charcoal-900/40 backdrop-blur-sm px-3 py-1.5 whitespace-nowrap">
          {String(index + 1).padStart(2, '0')}
        </span>
      </div>
    </div>
  );

  const textContent = (
    <div className="flex flex-col justify-center py-10 md:py-16 px-6 md:px-12 lg:px-16">
      <div className={`transition-all duration-800 ease-editorial ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
        <div className="flex items-center gap-3 mb-5">
          <div className="w-8 h-8 flex items-center justify-center">
            <i className={`${group.icon} text-lg text-terracotta-500`}></i>
          </div>
          <span className="font-mono text-[10px] text-stone-400 tracking-[0.3em] uppercase whitespace-nowrap">
            Service {String(index + 1).padStart(2, '0')}
          </span>
        </div>

        <h3 className="font-serif text-3xl md:text-4xl lg:text-5xl text-charcoal-900 mb-5 leading-tight tracking-tight">
          {group.title}
        </h3>

        <p className="text-base text-stone-500 leading-relaxed mb-7 max-w-lg">
          {group.intro}
        </p>

        <div className="w-12 h-[2px] bg-terracotta-400 mb-8" />

        <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-3.5">
          {group.items.map((item: string) => (
            <li
              key={item}
              className="flex items-start gap-3 text-sm text-stone-600 leading-relaxed group/item"
            >
              <span className="mt-1.5 w-1.5 h-1.5 bg-terracotta-400/60 shrink-0 transition-all duration-300 group-hover/item:bg-terracotta-500 group-hover/item:w-2 group-hover/item:h-2 group-hover/item:mt-[5px]" />
              <span className="transition-colors duration-300 group-hover/item:text-charcoal-800">{item}</span>
            </li>
          ))}
        </ul>

        <Link
          to={group.path || '/services'}
          className="inline-flex items-center gap-2 mt-8 text-sm text-terracotta-600 hover:text-terracotta-700 transition-colors duration-300 group/link whitespace-nowrap"
        >
          <span>Explore {group.title}</span>
          <i className="ri-arrow-right-line text-base transition-transform duration-300 group-hover/link:translate-x-1"></i>
        </Link>
      </div>
    </div>
  );

  return (
    <div
      ref={ref}
      className={`grid grid-cols-1 lg:grid-cols-2 transition-opacity duration-800 ease-editorial ${
        isVisible ? 'opacity-100' : 'opacity-0'
      }`}
    >
      {isEven ? (
        <>
          <div className="order-2 lg:order-1">{textContent}</div>
          <div className="order-1 lg:order-2">{imageContent}</div>
        </>
      ) : (
        <>
          <div className="order-2">{textContent}</div>
          <div className="order-1 lg:order-2">{imageContent}</div>
        </>
      )}
    </div>
  );
}

export default function ServicesSection() {
  return (
    <section id="services" className="relative bg-cream-50">
      <div className="max-w-[1600px] mx-auto px-0">
        <div className="pt-28 md:pt-36 pb-16 md:pb-20 px-6 md:px-12 lg:px-16">
          <p className="font-mono text-xs text-stone-400 tracking-[0.25em] uppercase mb-5 whitespace-nowrap">
            What We Do
          </p>
          <h2 className="font-serif text-4xl md:text-6xl lg:text-7xl text-charcoal-900 leading-tight max-w-3xl">
            Connected services for every stage of your project.
          </h2>
          <p className="text-stone-500 text-base mt-6 max-w-xl leading-relaxed">
            From the first sketch to detailed drawings — building design, interiors, project management, branding and digital, all under one roof.
          </p>
        </div>

        <div className="border-t border-stone-200/60">
          {serviceGroups.map((group, idx) => (
            <ServiceRow
              key={group.title}
              group={group}
              index={idx}
              isEven={idx % 2 === 0}
            />
          ))}
        </div>
      </div>
    </section>
  );
}
